import React, { useState, useMemo, useRef, useEffect } from 'react';
import { FaUserPlus } from 'react-icons/fa';
import { opcoesDeHorarioDoPonto, inicioEmMinutos } from '../horariosPonto';

const DIA_PONTO_PARA_GETDAY = { Dom: 0, Seg: 1, Ter: 2, Qua: 3, Qui: 4, Sex: 5, Sab: 6 };

const isMesmoDia = (dataA, dataB) => (
  dataA instanceof Date &&
  dataB instanceof Date &&
  dataA.getFullYear() === dataB.getFullYear() &&
  dataA.getMonth() === dataB.getMonth() &&
  dataA.getDate() === dataB.getDate()
);

const isHorarioNaoPermitido = (dataDesignacao, horario, agora = new Date()) => {
  if (!(dataDesignacao instanceof Date) || Number.isNaN(dataDesignacao.getTime())) {
    return false;
  }

  if (!isMesmoDia(dataDesignacao, agora)) {
    return false;
  }

  const minutosInicio = inicioEmMinutos(horario);
  if (minutosInicio === null) {
    return false;
  }

  const inicioHorario = new Date(
    agora.getFullYear(),
    agora.getMonth(),
    agora.getDate(),
    Math.floor(minutosInicio / 60),
    minutosInicio % 60,
    0,
    0
  );

  return agora >= inicioHorario;
};

const TrashSvg = ({ title, onClick, className, style }) => (
  <svg width="19" height="26" viewBox="0 0 19 26" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={onClick} className={className} style={style} role="img" aria-label={title}>
    <path d="M3.16602 7.39795H15.8327" stroke="#DB1212" strokeWidth="1.57" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7.91602 11.625V17.966" stroke="#DB1212" strokeWidth="1.57" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M11.084 11.625V17.966" stroke="#DB1212" strokeWidth="1.57" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3.95898 7.39795L4.75065 20.08C4.75065 20.6406 4.91747 21.1782 5.2144 21.5746C5.51133 21.971 5.91406 22.1937 6.33398 22.1937H12.6673C13.0872 22.1937 13.49 21.971 13.7869 21.5746C14.0838 21.1782 14.2507 20.6406 14.2507 20.08L15.0423 7.39795" stroke="#DB1212" strokeWidth="1.57" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7.125 7.39775V4.22725C7.125 3.94696 7.20841 3.67815 7.35687 3.47995C7.50534 3.28176 7.7067 3.17041 7.91667 3.17041H11.0833C11.2933 3.17041 11.4947 3.28176 11.6431 3.47995C11.7916 3.67815 11.875 3.94696 11.875 4.22725V7.39775" stroke="#DB1212" strokeWidth="1.57" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CardNovaDesignacao = ({
  styles,
  dataDesignacao,
  setDataDesignacao,
  periodos,
  togglePeriodo,
  setModalOpen,
  publicadoresSelecionados,
  setPublicadoresSelecionados,
  pontos,
  pontoSelecionado,
  setPontoSelecionado,
  handleChangePonto,
  regioes,
  regiaoSelecionada,
  setRegiaoSelecionada,
  handleGravar,
  editandoId,
  errors = {},
  setErrors = () => {},
  publicadoresSemDisponibilidade = [],
  perfilUsuario = [],
  mensagemConflito = '',
  validarDisponibilidadeSemana = null,
  validarDisponibilidade = null,
  handleUserInteraction = () => {},
  setEditandoId = null,
  handleCancelarEdicao = () => {}
}) => {
  const {
    Card,
    CardHeader,
    CardBody,
    FormGroup,
    CampoDateWrapper,
    CampoDatePicker,
    CampoSelect,
    PeriodoWrapper,
    PeriodoLabel,
    PeriodoRow,
    TimeButton,
    AddButton,
    PublicadorTable,
    PublicadorThead,
    PublicadorTbody,
    PublicadorTr,
    PublicadorTh,
    PublicadorTd,
    CheckboxWrapper,
    GravarButton,
    CancelarButton
  } = styles;

  const [saveAttempted, setSaveAttempted] = useState(false);
  const validacaoEmAndamento = useRef(false);
  const [validando, setValidando] = useState(false);
  const [agora, setAgora] = useState(() => new Date());
  
  // Mensagens de alerta padronizadas (exibidas em caixas iguais)
  const [alertMessages, setAlertMessages] = useState([]);
  
  // Estado local para rastrear publicadores com indisponibilidade na semana
  const [publicadoresComIndisponibilidade, setPublicadoresComIndisponibilidade] = useState([]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setAgora(new Date());
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, []);

  // Memoizar horariosTodos para evitar recriação em cada render
  const horariosTodos = useMemo(() => [
    ...(periodos?.manha || []),
    ...(periodos?.tarde || []),
    ...(periodos?.noite || [])
  ], [periodos?.manha, periodos?.tarde, periodos?.noite]);

  // Serializar dados para comparação (cria novo array antes de ordenar)
  const publicadoresIds = useMemo(() => 
    publicadoresSelecionados.map(p => p._id).sort().join(','),
    [publicadoresSelecionados]
  );
  
  const horariosSerializados = useMemo(() => 
    [...horariosTodos].sort().join('|'), 
    [horariosTodos]
  );

  const horariosNaoPermitidosSelecionados = useMemo(() => (
    horariosTodos.filter((horario) => isHorarioNaoPermitido(dataDesignacao, horario, agora))
  ), [agora, dataDesignacao, horariosTodos]);

  const horariosNaoPermitidosStr = useMemo(() => (
    horariosNaoPermitidosSelecionados.slice().sort().join('|')
  ), [horariosNaoPermitidosSelecionados]);

  // Validação de disponibilidade na semana do mês
  useEffect(() => {
    if (validacaoEmAndamento.current) return;
    if (editandoId && !saveAttempted) return;
    
    // Se não há data, publicadores ou horários, limpa e retorna
    if (!dataDesignacao || !publicadoresIds || !horariosSerializados) {
      setPublicadoresComIndisponibilidade([]);
      return;
    }

    const validar = async () => {
      if (!validarDisponibilidadeSemana || publicadoresSelecionados.length === 0) {
        setPublicadoresComIndisponibilidade([]);
        return;
      }
      
      validacaoEmAndamento.current = true;
      setValidando(true);
      try {
        const resultado = await validarDisponibilidadeSemana(dataDesignacao, publicadoresSelecionados);
        if (Array.isArray(resultado)) {
          setPublicadoresComIndisponibilidade(resultado);
        } else {
          setPublicadoresComIndisponibilidade([]);
        }
      } catch (error) {
        console.error('Erro ao validar disponibilidade semana:', error);
        setPublicadoresComIndisponibilidade([]);
      } finally {
        validacaoEmAndamento.current = false;
        setValidando(false);
      }
    };

    const timeoutId = setTimeout(validar, 100);
    return () => clearTimeout(timeoutId);
  }, [dataDesignacao, publicadoresIds, horariosSerializados, editandoId, saveAttempted]);

  // Validação de disponibilidade no dia da semana
  useEffect(() => {
    if (editandoId && !saveAttempted) return;
    if (!dataDesignacao || !pontoSelecionado || !publicadoresIds || !horariosSerializados) return;
    if (!validarDisponibilidade) return;

    const validar = async () => {
      try {
        await validarDisponibilidade(pontoSelecionado, periodos, dataDesignacao, publicadoresSelecionados);
      } catch (error) {
        console.error('Erro ao validar disponibilidade data:', error);
      }
    };

    const timeoutId = setTimeout(validar, 100);
    return () => clearTimeout(timeoutId);
  }, [dataDesignacao, pontoSelecionado, horariosSerializados, publicadoresIds, editandoId, saveAttempted]);

  useEffect(() => {
    if (horariosNaoPermitidosSelecionados.length === 0) {
      return;
    }

    horariosNaoPermitidosSelecionados.forEach((horario) => {
      if (periodos.manha.includes(horario)) {
        togglePeriodo('manha', horario);
      }
      if (periodos.tarde.includes(horario)) {
        togglePeriodo('tarde', horario);
      }
      if (periodos.noite.includes(horario)) {
        togglePeriodo('noite', horario);
      }
    });

    setErrors(prev => ({
      ...prev,
      periodo: 'Os horários já iniciados hoje não podem mais ser utilizados.'
    }));
  }, [horariosNaoPermitidosStr]);

  // Serializar dados para detectar mudanças nas mensagens
  const msgHorarioDetalhesStr = useMemo(() => 
    JSON.stringify(errors?.msgHorarioDetalhes || []),
    [errors?.msgHorarioDetalhes]
  );
  
  const msgDataIdsStr = useMemo(() => 
    (errors?.msgDataIds || []).sort().join(','),
    [errors?.msgDataIds]
  );
  
  const publicadoresComIndispStr = useMemo(() => 
    JSON.stringify(publicadoresComIndisponibilidade),
    [publicadoresComIndisponibilidade]
  );

  // Gera mensagens de alerta baseadas nos erros e validações
  useEffect(() => {
    if (editandoId && !saveAttempted) {
      setAlertMessages([]);
      return;
    }

    const msgs = [];

    // Mensagens de indisponibilidade de horário
    if (errors?.msgHorarioDetalhes && Array.isArray(errors.msgHorarioDetalhes)) {
      errors.msgHorarioDetalhes.forEach(det => {
        const pub = publicadoresSelecionados.find(p => p._id === det.id);
        if (!pub) return;
        
        const faltantes = (det.faltantes || []).filter(h => horariosTodos.includes(h));
        if (faltantes.length === 0) return;
        
        const nome = pub.nome || pub._id;
        msgs.push(`Indisponibilidade de horário: ${nome} no horário ${faltantes.join(', ')}.`);
      });
    }

    // Mensagens de indisponibilidade no dia da semana
    if (errors?.msgDataIds && Array.isArray(errors.msgDataIds) && errors.msgDataIds.length > 0) {
      const nomes = publicadoresSelecionados
        .filter(p => errors.msgDataIds.includes(p._id))
        .map(p => p.nome || p._id);
      
      if (nomes.length > 0) {
        msgs.push(`Indisponibilidade no dia da semana: ${nomes.join(', ')}.`);
      }
    }

    // Mensagens de indisponibilidade na semana do mês
    if (publicadoresComIndisponibilidade && publicadoresComIndisponibilidade.length > 0) {
      publicadoresComIndisponibilidade.forEach(item => {
        const pub = publicadoresSelecionados.find(p => p._id === item.id);
        if (!pub) return;
        
        const horariosFiltrados = (item.horarios || []).filter(h => horariosTodos.includes(h));
        if (horariosFiltrados.length === 0) return;
        
        const nome = pub.nome || pub._id;
        let formatted = '';
        
        if (horariosFiltrados.length === 1) {
          formatted = horariosFiltrados[0];
        } else {
          const last = horariosFiltrados[horariosFiltrados.length - 1];
          const rest = horariosFiltrados.slice(0, -1);
          formatted = `${rest.join(', ')} e ${last}`;
        }
        
        msgs.push(`Indisponibilidade na semana do mês: ${nome} no horário de ${formatted}.`);
      });
    }

    // Mensagens gerais
    if (errors?.conflito) msgs.push(errors.conflito);
    if (mensagemConflito) msgs.push(mensagemConflito);
    if (errors?.publicadores) msgs.push(errors.publicadores);

    // Só atualiza se as mensagens mudaram
    setAlertMessages(prev => {
      const prevStr = JSON.stringify(prev);
      const newStr = JSON.stringify(msgs);
      return prevStr === newStr ? prev : msgs;
    });
  }, [
    msgHorarioDetalhesStr,
    msgDataIdsStr,
    errors?.conflito,
    errors?.publicadores,
    mensagemConflito,
    publicadoresComIndispStr,
    publicadoresIds,
    horariosSerializados,
    editandoId,
    saveAttempted
  ]);
  
  // Calcula se existem erros que impedem salvar
  const hasErrors = useMemo(() => {
    // Verifica erros de disponibilidade
    const temIndispSemana = publicadoresComIndisponibilidade.length > 0;
    const temIndispDia = errors?.msgDataIds && errors.msgDataIds.length > 0;
    const temIndispHorario = errors?.msgHorarioDetalhes && errors.msgHorarioDetalhes.length > 0;
    
    return Boolean(
      errors.data || 
      errors.ponto || 
      errors.periodo || 
      errors.publicadores || 
      errors.conflito || 
      mensagemConflito ||
      temIndispSemana ||
      temIndispDia ||
      temIndispHorario
    );
  }, [
    errors.data,
    errors.ponto,
    errors.periodo,
    errors.publicadores,
    errors.conflito,
    errors?.msgDataIds,
    errors?.msgHorarioDetalhes,
    mensagemConflito,
    publicadoresComIndisponibilidade
  ]);

  // Em modo de edição, permite salvar mesmo com erros que não foram causados por interação do usuário
  const realHasErrors = editandoId && !saveAttempted ? false : hasErrors;
  
  const canSave = useMemo(() => 
    Boolean(pontoSelecionado) && 
    Boolean(dataDesignacao) && 
    horariosTodos.length > 0 && 
    horariosNaoPermitidosSelecionados.length === 0 &&
    publicadoresSelecionados.length >= 2 && 
    !realHasErrors &&
    !validando,
    [pontoSelecionado, dataDesignacao, horariosTodos.length, horariosNaoPermitidosSelecionados.length, publicadoresSelecionados.length, realHasErrors, validando]
  );
  const hoje = new Date();
  const hojeSemHora = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());

  const pontoObjeto = useMemo(
    () => (pontos || []).find(p => String(p._id) === String(pontoSelecionado)) || null,
    [pontos, pontoSelecionado]
  );
  const opcoesHorario = useMemo(
    () => opcoesDeHorarioDoPonto(pontoObjeto, horariosTodos),
    [pontoObjeto, horariosTodos]
  );

  const diasSemanaPermitidos = useMemo(() => {
    const dias = (pontoObjeto?.dias || []).map(d => DIA_PONTO_PARA_GETDAY[d]).filter(n => n !== undefined);
    return dias.length ? dias : null;
  }, [pontoObjeto]);

  // Ao trocar de ponto, descarta a data se ele não funciona nesse dia da semana.
  useEffect(() => {
    if (editandoId || !diasSemanaPermitidos || !(dataDesignacao instanceof Date)) return;
    if (!diasSemanaPermitidos.includes(dataDesignacao.getDay())) {
      setDataDesignacao(null);
    }
  }, [diasSemanaPermitidos]);

  const getTimeButtonProps = (periodoNome, horario) => {
    const disabled = isHorarioNaoPermitido(dataDesignacao, horario, agora);
    const selected = periodos[periodoNome].includes(horario);

    return {
      className: selected ? 'selected' : '',
      disabled,
      title: disabled ? 'Esse horário já começou e não pode mais ser usado hoje.' : undefined,
      style: disabled ? { backgroundColor: '#c0c0c0', cursor: 'not-allowed', opacity: 0.7 } : undefined,
      onClick: () => {
        if (disabled) {
          setErrors(prev => ({
            ...prev,
            periodo: 'Os horários já iniciados hoje não podem mais ser utilizados.',
            conflito: ''
          }));
          return;
        }

        if (handleUserInteraction) handleUserInteraction();
        togglePeriodo(periodoNome, horario);
        setErrors(prev => ({ ...prev, periodo: '', conflito: '' }));
      }
    };
  };

  const userId = (typeof window !== 'undefined') ? localStorage.getItem('idUsuario') : null;
  const regioesParaMostrar = useMemo(() => {
    const sortRegioes = (arr) => (arr || []).slice().sort((a, b) => {
      const nomeA = (a?.nome || '').toLowerCase();
      const nomeB = (b?.nome || '').toLowerCase();
      // numeric:true melhora ordenação de códigos como DF-001, DF-010
      return nomeA.localeCompare(nomeB, 'pt-BR', { numeric: true, sensitivity: 'base' });
    });

    // Admin e Coordenador-Geral veem todas as regiões ordenadas
    if (perfilUsuario && (perfilUsuario.includes('admin') || perfilUsuario.includes('coordenador-geral'))) return sortRegioes(regioes);
    if (!regioes || regioes.length === 0) return [];
    if (!pontos || pontos.length === 0) return [];
    if (!userId) return [];

    const regionIds = new Set();
    pontos.forEach(ponto => {
      const coordenadores = ponto.coordenadores || [];
      const isCoordenador = coordenadores.some(c => {
        if (!c) return false;
        if (typeof c === 'string') return String(c) === String(userId);
        if (c._id) return String(c._id) === String(userId);
        return String(c) === String(userId);
      });
      if (isCoordenador) {
        const regId = ponto.regiao?._id || ponto.regiao;
        if (regId) regionIds.add(String(regId));
      }
    });

    return sortRegioes(regioes.filter(r => regionIds.has(String(r._id))));
  }, [regioes, pontos, userId, perfilUsuario]);

  // Memoizar pontos filtrados por região
  const pontosFiltrados = useMemo(() => {
    if (!pontos || pontos.length === 0) return [];
    
    const filtered = !regiaoSelecionada ? pontos : pontos.filter(ponto => {
      const regId = ponto.regiao?._id || ponto.regiao;
      return String(regId) === String(regiaoSelecionada);
    });
    
    return filtered.sort((a, b) => {
      const nomeA = (a.nome || '').toLowerCase();
      const nomeB = (b.nome || '').toLowerCase();
      return nomeA.localeCompare(nomeB, 'pt-BR');
    });
  }, [pontos, regiaoSelecionada]);

  return (
    <Card>
      <CardHeader>{editandoId ? 'Editar Designação' : 'Nova Designação'}</CardHeader>
      <CardBody>
        <FormGroup>
          <label>Data:{errors.data ? <span style={{ color: 'red', marginLeft: 6 }}>*</span> : null}</label>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <CampoDateWrapper>
              <CampoDatePicker
                value={dataDesignacao}
                onChange={(d) => {
                  if (handleUserInteraction) handleUserInteraction();
                  setDataDesignacao(d);
                  // limpa todos os erros relacionados à data/semana/horário para recalcular do zero
                  setErrors(prev => ({
                    ...prev,
                    data: '',
                    msgData: '',
                    msgDataIds: [],
                    msgHorario: '',
                    msgHorarioIds: [],
                    periodo: '',
                    conflito: ''
                  }));
                  setPublicadoresComIndisponibilidade([]);
                  setAlertMessages([]);
                }}
                minDate={hojeSemHora}
                diasSemanaPermitidos={diasSemanaPermitidos}
                placeholderText="DD/MM/AAAA"
              />
            </CampoDateWrapper>
            {errors.data && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{errors.data}</div>}
            {errors.msgData && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{errors.msgData}</div>}
          </div>
        </FormGroup>
        <FormGroup>
          <label>Região:</label>
          <CampoSelect
            value={regiaoSelecionada}
            onChange={e => {
              if (handleUserInteraction) handleUserInteraction();
              setRegiaoSelecionada(e.target.value);
              setPontoSelecionado("");
              setErrors(prev => ({ ...prev, ponto: '' }));
            }}
          >
            <option value="">Selecione a região</option>
            {regioesParaMostrar && regioesParaMostrar.length > 0 && regioesParaMostrar.map(regiao => (
              <option key={regiao._id} value={regiao._id}>{regiao.nome}</option>
            ))}
          </CampoSelect>
        </FormGroup>
        <FormGroup>
          <label>Ponto:{errors.ponto ? <span style={{ color: 'red', marginLeft: 6 }}>*</span> : null}</label>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <CampoSelect
              value={pontoSelecionado}
              onChange={async e => {
                if (handleUserInteraction) handleUserInteraction();
                const novo = e.target.value;
                if (handleChangePonto) {
                  await Promise.resolve(handleChangePonto(novo));
                } else {
                  setPontoSelecionado(novo);
                  setErrors(prev => ({ ...prev, ponto: '' }));
                }
              }}
            >
              <option value="">Selecione um ponto</option>
              {pontosFiltrados.map(ponto => (
                <option key={ponto._id} value={ponto._id}>{ponto.nome}</option>
              ))}
            </CampoSelect>
            {errors.ponto && <div style={{ color: 'red', fontSize: 12, marginTop: 6 }}>{errors.ponto}</div>}
          </div>
        </FormGroup>
  <PeriodoWrapper>
      <PeriodoLabel>Período:{errors.periodo ? <span style={{ color: 'red', marginLeft: 6 }}>*</span> : null}</PeriodoLabel>
          {!pontoSelecionado ? (
            <div style={{ flex: 1, fontSize: 13, color: '#666', alignSelf: 'center' }}>
              Selecione um ponto para ver os horários disponíveis.
            </div>
          ) : (
            [['manha', 'Manhã'], ['tarde', 'Tarde'], ['noite', 'Noite']]
              .filter(([chave]) => opcoesHorario[chave].length > 0)
              .map(([chave, titulo], idx) => (
              <React.Fragment key={chave}>
                {idx > 0 && <PeriodoLabel></PeriodoLabel>}
                <PeriodoRow>
                  <span>{titulo}:</span>
                  {opcoesHorario[chave].map(h => (
                    <TimeButton key={h} {...getTimeButtonProps(chave, h)}>{h}</TimeButton>
                  ))}
                </PeriodoRow>
              </React.Fragment>
            ))
          )}
        </PeriodoWrapper>
        {errors.periodo && (
          <div style={{ color: 'red', fontSize: 12, margin: '2px 0 4px 70px' }}>{errors.periodo}</div>
        )}
        <AddButton 
          disabled={publicadoresSelecionados.length >= 3 || !pontoSelecionado || !dataDesignacao || horariosTodos.length === 0}
          style={(publicadoresSelecionados.length >= 3 || !pontoSelecionado || !dataDesignacao || horariosTodos.length === 0) ? { backgroundColor: '#c0c0c0', cursor: 'not-allowed' } : undefined}
          title={(!pontoSelecionado || !dataDesignacao || horariosTodos.length === 0) ? 'Selecione ponto, data e horário antes de adicionar publicadores' : undefined}
          onClick={() => {
            if (publicadoresSelecionados.length >= 3 || !pontoSelecionado || !dataDesignacao || horariosTodos.length === 0) return;
            setModalOpen(true);
          }}>
          Adicionar Publicador <FaUserPlus />
        </AddButton>
  <PublicadorTable>
          <PublicadorThead>
            <PublicadorTr>
              <PublicadorTh>Publicador</PublicadorTh>
              <PublicadorTh>Congregação</PublicadorTh>
              <PublicadorTh>Remover</PublicadorTh>
            </PublicadorTr>
          </PublicadorThead>
          <PublicadorTbody>
            {publicadoresSelecionados && publicadoresSelecionados.length > 0 ? (
              publicadoresSelecionados.map(pub => {
                const semDispSemana = publicadoresComIndisponibilidade.some(item => item.id === pub._id);
                const semDispData = errors.msgDataIds?.includes?.(pub._id);
                const semDispHorario = (errors.publicadores === '' && errors.msgHorario) ? errors.msgHorarioIds?.includes?.(pub._id) : false;
                const any = semDispSemana || semDispData || semDispHorario;
                return (
                  <PublicadorTr key={pub._id} style={ any ? { background:'#ffecec' } : undefined }>
                    <PublicadorTd style={ any ? { color:'#b30000', fontWeight:'500' } : undefined }>{pub.nome}</PublicadorTd>
                    <PublicadorTd style={ any ? { color:'#b30000' } : undefined }>{pub.congregacao?.nome || '-'}</PublicadorTd>
                    <PublicadorTd>
                      <TrashSvg
                        title="Remover publicador"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setPublicadoresSelecionados(prev => {
                            const filtered = prev.filter(p => p._id !== pub._id);
                            if (filtered.length >= 2) setErrors(prevE => ({ ...prevE, publicadores: '' }));
                            return filtered;
                          });
                          setErrors(prevE => {
                            const newErrors = { ...prevE };
                            
                            const remaining = publicadoresSelecionados.filter(p => p._id !== pub._id);
                            if (remaining.length >= 2) {
                              newErrors.publicadores = '';
                            }
                            
                            if (newErrors.msgHorarioIds && Array.isArray(newErrors.msgHorarioIds)) {
                              const newIds = newErrors.msgHorarioIds.filter(id => id !== pub._id);
                              if (newIds.length === 0) {
                                newErrors.msgHorario = '';
                                newErrors.msgHorarioIds = [];
                              } else {
                                newErrors.msgHorarioIds = newIds;
                              }
                            }
                            
                            if (newErrors.msgDataIds && Array.isArray(newErrors.msgDataIds)) {
                              const newIds = newErrors.msgDataIds.filter(id => id !== pub._id);
                              if (newIds.length === 0) {
                                newErrors.msgData = '';
                                newErrors.msgDataIds = [];
                              } else {
                                newErrors.msgDataIds = newIds;
                              }
                            }
                            
                            return newErrors;
                          });
                        }}
                      />
                    </PublicadorTd>
                  </PublicadorTr>
                );
              })
            ) : (
              <PublicadorTr>
                <PublicadorTd colSpan={3} style={{ textAlign: 'center', color: '#666' }}>
                  Nenhum publicador selecionado
                </PublicadorTd>
              </PublicadorTr>
            )}
            {/* Mensagens na tabela removidas em favor do padrão unificado abaixo */}
          </PublicadorTbody>
        </PublicadorTable>
        {alertMessages && alertMessages.length > 0 && (
          <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
            {alertMessages.map((msg, idx) => (
              <div key={idx} style={{
                color: '#b3261e',
                fontSize: 13,
                padding: '8px 12px',
                backgroundColor: '#fff6f6',
                borderRadius: 8,
                border: '1px solid #f5c6c6',
                textAlign: 'center',
                fontWeight: 500,
                lineHeight: 1.4
              }}>
                {msg}
              </div>
            ))}
          </div>
        )}
        {/* TODO: Funcionalidade despriorizada para etapa 1 */}
        {/* <CheckboxWrapper>
          <input type="checkbox" id="repetir" />
          <label htmlFor="repetir">Repetir essa designação por 2 meses</label>
        </CheckboxWrapper> */}
  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
  <GravarButton
    disabled={!canSave || realHasErrors}
    onClick={async () => {
      if (handleUserInteraction) handleUserInteraction();
      
      // Valida disponibilidade antes de gravar
      if (dataDesignacao && validarDisponibilidadeSemana && publicadoresSelecionados.length > 0 && pontoSelecionado && horariosTodos.length > 0) {
        try {
          if (horariosNaoPermitidosSelecionados.length > 0) {
            setSaveAttempted(true);
            setErrors(prev => ({
              ...prev,
              periodo: 'Os horários já iniciados hoje não podem mais ser utilizados.'
            }));
            return;
          }

          const resultado = await validarDisponibilidadeSemana(dataDesignacao, publicadoresSelecionados);
          if (resultado && resultado.length > 0) {
            // Se há indisponibilidade, apenas exibe mensagem (já gerenciada pelo useEffect)
            setSaveAttempted(true);
            return;
          }
        } catch (error) {
          console.error('Erro ao validar antes de gravar:', error);
        }
      }
      
      // Limpa conflitos
      setErrors(prev => ({ ...prev, conflito: '' }));
      
      // Verifica se há mensagem de conflito
      if (mensagemConflito) {
        setSaveAttempted(true);
        return;
      }
      
      // Valida campos obrigatórios
      if (!canSave) {
        setSaveAttempted(true);
        setErrors(prev => ({
          ...prev,
          data: prev.data || (!dataDesignacao ? 'Data é obrigatória.' : ''),
          ponto: prev.ponto || (!pontoSelecionado ? 'Ponto é obrigatório.' : ''),
          periodo: prev.periodo || (horariosTodos.length === 0 ? 'Selecione ao menos um horário.' : ''),
          publicadores: prev.publicadores || (publicadoresSelecionados.length < 2 ? 'Selecione pelo menos 2 publicadores.' : '')
        }));
        return; 
      }

      // Valida o ponto se necessário
      if (handleChangePonto) {
        try {
          const msg = await Promise.resolve(handleChangePonto(pontoSelecionado));
          if (msg) {
            setSaveAttempted(true);
            return;
          }
        } catch (e) {
          console.error('Erro ao validar ponto:', e);
        }
      }

      // Executa o salvamento
      try {
        await Promise.resolve(handleGravar());
        setSaveAttempted(false);
      } catch (error) {
        console.error('Erro ao gravar:', error);
        setSaveAttempted(true);
      }
    }}
    style={ !canSave || realHasErrors ? { backgroundColor: '#c0c0c0', cursor: 'not-allowed' } : undefined }
  >{editandoId ? 'Atualizar' : 'Gravar'}</GravarButton>
  {editandoId && (
    <CancelarButton
      onClick={() => {
        if (typeof handleCancelarEdicao === 'function') {
          handleCancelarEdicao();
        }
      }}
    >
      Cancelar
    </CancelarButton>
  )}
  </div>
      </CardBody>
    </Card>
  );
};

export default CardNovaDesignacao;
